import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import { DyePenetrantInspectionItem } from '../../../utils/models';
import { getLPTColumns } from '../../../utils/data';

import AutTable from '../../blocks/aut-grid-table/index';
import DyeInspectionItemForm from './dyeInspectionItemForm';
import Form from '../../common/form';

class DyeInspectionItem extends Form {
	state = {
		data: initialData,
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		surfaceCondition: Joi.string().min(3).required().label('Surface Condition'),
		developer: Joi.string().min(3).required().label('Developer'),
		method: Joi.string().min(3).required().label('Method'),
		thickness: Joi.number().min(0).required().label('Thickness (mm)'),
		material: Joi.string().required().label('Material'),
		dwellTime: Joi.string().min(3).required().label('Dwell Time'),
		penetrant: Joi.string().min(3).required().label('Penentrant'),
		developingTime: Joi.string().min(3).required().label('Developing Time'),
		penetrantRemover: Joi.string().min(3).required().label('Penetrant Remover'),
		refStd: Joi.string().min(3).required().label('Ref Std'),
		acceptanceStd: Joi.string().min(3).required().label('Acceptance Std'),
		procedureNo: Joi.string().min(3).required().label('Procedure No'),
		summary: Joi.string().min(3).required().label('Summary'),
		data: Joi.array()
			.min(1)
			.items(
				Joi.object({
					srNo: Joi.number().positive().required().label('SR No'),
					itemInspected: Joi.string().min(3).required().label('Item Inspected'),
					size: Joi.number().min(1).required().label('Size (Inches)'),
					welderNo: Joi.string().required().label('Welder No'),
					observation: Joi.string().min(10).required().label('Observation'),
					result: Joi.string().min(3).required().label('Result'),
				})
			)
			.required()
			.label('Inspection Items'),
	};

	static propTypes = {
		onAdd: PropTypes.func,
	};

	onAddItem = (item: DyePenetrantInspectionItem) => {
		const data: any = { ...this.state.data };
		data.data = [...data.data];
		data.data.push({ ...item, srNo: this.state.data.data.length + 1 });

		this.setState({ data });
	};

	onDeleteItem = (item: DyePenetrantInspectionItem): void => {
		const data = { ...this.state.data };
		data.data = data.data.filter(
			(i: DyePenetrantInspectionItem) => i.srNo !== item.srNo
		);

		this.setState({ data });
	};

	doSubmit = (): void => {
		const props: any = this.props;
		props.onAdd(this.state.data);

		this.setState({ data: initialData });
	};

	render() {
		return (
			<React.Fragment>
				<DyeInspectionItemForm onAdd={this.onAddItem} />

				<div className="divider my-4"></div>

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<div className="form-title">Dye Inspection Overview</div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'surfaceCondition',
								label: 'Surface Condition',
								placeholder: 'Enter Surface Condition',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'developer',
								label: 'Developer',
								placeholder: 'Enter Developer Name',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'method',
								label: 'Method',
								placeholder: 'Enter Inspection Method',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'dwellTime',
								label: 'Dwell Time',
								placeholder: 'e.g. 10 minutes',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'penetrant',
								label: 'Penetrant',
								placeholder: 'Enter Penetrant',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'developingTime',
								label: 'Developing Time',
								placeholder: 'e.g. 10 minutes',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'thickness',
								label: 'Thickness (mm)',
								placeholder: 'Enter Thickness (mm)',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'material',
								label: 'Material',
								placeholder: 'Enter Material',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'penetrantRemover',
								label: 'Penetrant Remover',
								placeholder: 'Enter Penetrant Remover',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'refStd',
								label: 'Ref Standard',
								placeholder: 'Enter Ref Standard',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'acceptanceStd',
								label: 'Acceptance Standard',
								placeholder: 'Enter Acceptance Standard',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'procedureNo',
								label: 'Procedure Number',
								placeholder: 'Enter Procedure Number',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					{this.renderTextArea({
						name: 'summary',
						placeholder: 'Enter Summary',
						label: 'Summary',
						rows: '6',
					})}

					{this.renderButton('Submit Overview')}
				</form>

				<div className="divider my-4"></div>

				<AutTable
					tableColumns={getLPTColumns()}
					data={this.state.data.data}
					onDeleteItem={this.onDeleteItem}
				/>
			</React.Fragment>
		);
	}
}

export default DyeInspectionItem;

const initialData = {
	surfaceCondition: '',
	thickness: '',
	material: '',
	developer: '',
	method: '',
	dwellTime: '',
	penetrant: '',
	developingTime: '',
	penetrantRemover: '',
	refStd: '',
	acceptanceStd: '',
	procedureNo: '',
	summary: '',
	data: [],
};
